import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Check, X } from "lucide-react";

const REASON_LABEL = {
  inapproprie: "Inapproprié",
  spam: "Spam",
  haine: "Haine / discrimination",
  faux: "Information fausse",
  autre: "Autre",
};

const STATUS_COLOR = {
  open: "bg-cef-pink text-white",
  resolved: "bg-cef-green",
  dismissed: "bg-neutral-300",
};

export default function ReportsTab() {
  const [reports, setReports] = React.useState([]);
  const [filter, setFilter] = React.useState("open");
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/admin/reports", { params: filter !== "all" ? { status: filter } : {} })
      .then((r) => setReports(r.data))
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, [filter]);
  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    await api.patch(`/admin/reports/${r.id}`, { status });
    toast.success(status === "resolved" ? "Marqué résolu" : "Marqué rejeté");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase flex items-center gap-2">
          <Flag className="w-6 h-6" /> Signalements ({reports.length})
        </h2>
        <div className="flex gap-2">
          {["open", "resolved", "dismissed", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`chip ${filter === s ? "bg-black text-white" : "bg-white"}`}
              data-testid={`reports-filter-${s}`}
            >
              {s === "open" ? "À traiter" : s === "resolved" ? "Résolus" : s === "dismissed" ? "Rejetés" : "Tous"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-neutral-500">Chargement…</div>}
      {!loading && reports.length === 0 && (
        <div className="neo-card p-5 text-neutral-500">Aucun signalement {filter !== "all" ? `(${filter})` : ""}.</div>
      )}

      <div className="grid gap-3" data-testid="reports-list">
        {reports.map((r) => (
          <div key={r.id} className="neo-card p-4" data-testid={`report-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`chip text-[10px] py-0.5 ${STATUS_COLOR[r.status] || "bg-white"}`}>{r.status}</span>
                  <span className="chip bg-cef-yellow text-[10px] py-0.5">{r.target_type}</span>
                  <span className="font-mono text-xs text-neutral-500">{r.target_id?.slice(0, 8)}</span>
                  <span className="chip bg-white text-[10px] py-0.5">{REASON_LABEL[r.reason] || r.reason}</span>
                </div>
                {r.message && <p className="mt-2 text-sm text-neutral-700 italic">« {r.message} »</p>}
                <div className="mt-2 text-xs text-neutral-500 flex flex-wrap gap-3">
                  {r.reporter_email && <span>📧 {r.reporter_email}</span>}
                  <span>{r.created_at ? new Date(r.created_at).toLocaleString("fr-BE") : ""}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {r.status !== "resolved" && (
                  <button onClick={() => setStatus(r, "resolved")} className="neo-btn bg-cef-green text-sm py-2" data-testid={`report-resolve-${r.id}`}>
                    <Check className="w-4 h-4" /> Résoudre
                  </button>
                )}
                {r.status !== "dismissed" && (
                  <button onClick={() => setStatus(r, "dismissed")} className="neo-btn bg-white text-sm py-2" data-testid={`report-dismiss-${r.id}`}>
                    <X className="w-4 h-4" /> Rejeter
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
